// eslint-disable-next-line no-restricted-imports
import { atomizeChangeset, diff, IChange } from "json-diff-ts";
import _ from "lodash";

import { getEnv } from "@services/configService";

type Value = object | string | null | undefined;

class CompareService {
    private readonly a: object;
    private readonly b: object;

    public readonly differences: IChange[];
    public readonly hasDifferences: boolean;

    constructor(a: Value, b: Value) {
        this.a = this.parseValue(a);
        this.b = this.parseValue(b);

        this.differences = this.getDifferences();
        this.hasDifferences = this.differences.length > 0;
    }

    private parseValue(value: Value): object {
        try {
            if (typeof value === "string") {
                return JSON.parse(value);
            }
            if (value === null || value === undefined) {
                return {};
            }
            return value;
        } catch (_error) {
            if (getEnv() === "development") {
                // eslint-disable-next-line no-console
                console.warn("[CompareService]: Invalid JSON on page: ", window.location.href);
            }
            return {};
        }
    }

    private getDifferences(): IChange[] {
        return diff(this.a, this.b);
    }

    /**
     * Reconstructs differences from a flat list into a nested object (paths → values).
     */
    public getDifferencesObject() {
        const result: Record<string, unknown> = {};

        const changes = atomizeChangeset(this.differences);

        for (const d of changes) {
            if (d.type === "REMOVE") {
                continue;
            }

            const path = d.path.split(".").slice(1) ?? [];
            if (path.length === 0) {
                continue;
            }

            let current: Record<string, unknown> = result;
            for (let i = 0; i < path.length - 1; i++) {
                const key = String(path[i]);
                if (!current[key]) {
                    current[key] = {};
                }
                current = current[key] as Record<string, unknown>;
            }

            const lastKey = String(path[path.length - 1]);
            current[lastKey] = d.value;
        }

        return result;
    }

    /**
     * Converts differences into a report format (op → paths).
     */
    public getDifferencesReport() {
        if (!this.hasDifferences) {
            return {};
        }

        const returnValue = atomizeChangeset(this.differences);
        const groupedByOperation = _.groupBy(returnValue, "type");
        return _.mapValues(groupedByOperation, (group) => group.map((item) => item.path.slice(2)));
    }
}

export default CompareService;
